"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExposureService = void 0;
const rbm_ott_sdk_1 = require("@ericssonbroadcastservices/rbm-ott-sdk");
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const device_1 = require("../device");
const common_1 = require("../device/common");
const KeySystemDrmMap = {
    "com.apple.fps": "fairplay",
    "com.widevine.alpha": "widevine",
    "com.microsoft.playready": "playready",
};
class ExposureService {
    constructor({ customer, businessUnit, baseUrl }) {
        this.context = {
            customer,
            businessUnit,
            baseUrl,
            errorFactory(response) {
                return __awaiter(this, void 0, void 0, function* () {
                    const responseBody = yield response.json();
                    const url = new URL(response.url);
                    const message = `HTTP Error: ${responseBody.message} (${response.status})`;
                    let errType = js_player_shared_1.ErrorTypes.OTHER;
                    if (url.pathname.endsWith("/auth/anonymous")) {
                        errType = js_player_shared_1.ErrorTypes.API_AUTH_ANON;
                    }
                    else if (url.pathname.endsWith("/auth/login")) {
                        errType = js_player_shared_1.ErrorTypes.API_AUTH;
                    }
                    else if (url.pathname.endsWith("/play")) {
                        errType = js_player_shared_1.ErrorTypes.API_PLAY_REQUEST;
                    }
                    else if (response.url.includes("/content/asset/")) {
                        errType = js_player_shared_1.ErrorTypes.API_ASSET;
                    }
                    throw new js_player_shared_1.APIError(message, { type: errType, code: response.status });
                });
            },
        };
        if (!customer || !businessUnit) {
            throw new js_player_shared_1.InitError("No Customer or BusinessUnit defined", {
                type: js_player_shared_1.ErrorTypes.OPTIONS,
            });
        }
    }
    assetMetadata(assetId) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = { Authorization: `Bearer ${this.sessionToken}` };
            return rbm_ott_sdk_1.getAsset.call(this.context, { assetId, headers });
        });
    }
    authenticate(authOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const deviceInfo = yield (0, device_1.getDevice)();
            const deviceType = deviceInfo.type === "ctv" ? "SMART_TV" : "WEB";
            const device = { type: deviceType, name: deviceInfo.name };
            if (authOptions === null || authOptions === void 0 ? void 0 : authOptions.username) {
                return rbm_ott_sdk_1.login.call(this.context, Object.assign(Object.assign({}, authOptions), { device: Object.assign({ deviceId: deviceInfo.id }, device) }));
            }
            return rbm_ott_sdk_1.loginAnonymous.call(this.context, {
                device,
                deviceId: deviceInfo.id,
            });
        });
    }
    playRequest({ assetId, sessionToken, adsOptions, audioOnly, adobePrimetimeToken, maxResolution, supportedFormatTypes, supportedKeySystem, materialProfile, start, end, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!sessionToken && !this.sessionToken) {
                throw new js_player_shared_1.InitError("No sessionToken exists", {
                    type: js_player_shared_1.ErrorTypes.OPTIONS,
                });
            }
            const supportedFormats = (supportedFormatTypes || []).flatMap((format) => {
                switch (format) {
                    case "DASH":
                        return "dash";
                    case "HLS":
                        return "hls";
                    case "MP3":
                        return "mp3";
                    case "SMOOTHSTREAMING":
                        return "mss";
                    default:
                        return [];
                }
            });
            const supportedDrms = [];
            if (supportedKeySystem) {
                supportedDrms.push(KeySystemDrmMap[supportedKeySystem]);
            }
            const headers = Object.assign({ Authorization: `Bearer ${sessionToken || this.sessionToken}` }, (adobePrimetimeToken && {
                "X-Adobe-Primetime-MediaToken": adobePrimetimeToken,
            }));
            const playResponse = yield rbm_ott_sdk_1.play.call(this.context, Object.assign(Object.assign({}, adsOptions), { headers,
                assetId,
                audioOnly,
                maxResolution,
                materialProfile, supportedFormats: supportedFormats
                    ? supportedFormats.join(",")
                    : undefined, supportedDrms: supportedDrms ? supportedDrms.join(",") : undefined, start,
                end }));
            return this.parsePlayResponse(playResponse);
        });
    }
    parsePlayResponse(playResponse) {
        var _a, _b, _c, _d, _e;
        const restrictions = playResponse === null || playResponse === void 0 ? void 0 : playResponse.contractRestrictions;
        if (restrictions === null || restrictions === void 0 ? void 0 : restrictions.maxBitrate) {
            restrictions.maxBitrate *= 1000;
        }
        if (restrictions === null || restrictions === void 0 ? void 0 : restrictions.minBitrate) {
            restrictions.minBitrate *= 1000;
        }
        // TODO: EMP-18317 remove this if statement once LG/Nowtilus supports https again...
        // 😐🔫 Workaround to enable playback on LG 2017-2019 TVs, certain Root CAs
        // aren't allowed on these devices...
        if ((0, common_1.isLGTV)() &&
            ((_a = playResponse.streamInfo) === null || _a === void 0 ? void 0 : _a.ssai) &&
            ((_b = playResponse.ads) === null || _b === void 0 ? void 0 : _b.stitcher) === "NOWTILUS") {
            (_d = (_c = playResponse.ads) === null || _c === void 0 ? void 0 : _c.clips) === null || _d === void 0 ? void 0 : _d.forEach((clip) => {
                var _a;
                (_a = clip.impressionUrlTemplates) === null || _a === void 0 ? void 0 : _a.forEach((impression, index) => {
                    if (clip.impressionUrlTemplates) {
                        clip.impressionUrlTemplates[index] = impression.replace("https://", "http://");
                    }
                    if (clip.trackingEvents) {
                        for (const trackingEvent in clip.trackingEvents) {
                            const event = clip.trackingEvents[trackingEvent];
                            event === null || event === void 0 ? void 0 : event.forEach((url, index) => {
                                event[index] = url.replace("https://", "http://");
                            });
                        }
                    }
                });
            });
            (_e = playResponse.formats) === null || _e === void 0 ? void 0 : _e.forEach((format) => {
                var _a;
                if (format.vastUrl) {
                    format.vastUrl = format.vastUrl.replace("https://", "http://");
                }
                format.mediaLocator = (_a = format.mediaLocator) === null || _a === void 0 ? void 0 : _a.replace("https://", "http://");
            });
        }
        return playResponse;
    }
}
exports.ExposureService = ExposureService;
//# sourceMappingURL=ExposureService.js.map